<script setup>
import { Tooltip } from "bootstrap";
import { defineProps } from "vue";
import CircleCenter from "@/components/CircleCenter.vue";
import { useOrganizationStore } from "@/stores/organization";
import { useMessengerStore } from "@/stores/messenger";
import { commonMarkToHtml, processCitations, } from "@/utils/utils";
import ImageThumbnail from "@/components/ImageThumbnail.vue";
import VideoThumbnail from "@/components/VideoThumbnail.vue";
import GenericThumbnail from "@/components/GenericThumbnail.vue";

const props = defineProps({
  messageObj: {
    type: Object,
    required: true,
  },
  isLast: {
    type: Boolean,
    required: true,
  },
});

// Tracks the dom element for the response
const responseEl = ref(null)

// Make a computed value for the message, so we can transform it
const response = computed(()=>{
  const {response, citations} = props.messageObj

  const html = commonMarkToHtml(response)
  return citations ? processCitations(html, citations) : html
})

watch(responseEl, async (newVal)=>{
  // need to wait till next tick, because updatedHtml has not reached the dom yet
  await nextTick()
  // if not null, that means the ele has appeared in the dom
  if(newVal) {
    newVal.querySelectorAll('[data-bs-toggle="tooltip"]')
    .forEach(el=>{
      // Dont update el, that already have _tooltip
      if(!el._tooltip) {
        el._tooltip = new Tooltip(el, {trigger: 'hover'})
      }
    })
  }
})

// get bot name
const organizationStore = useOrganizationStore();
const messengerStore = useMessengerStore();

// handle rate response
const handleRateResponse = (rating) => {
  let message = "";
  switch (rating) {
    case "good":
      message = "👍";
      break;
    case "bad":
      message = "👎";
      break;
    default:
      throw new Error("Invalid rating");
  }

  // send message
  messengerStore.sendMessage(message);
};
</script>

<template>
  <div class="bot-message-container">
    <div style="{width: 3rem;}">
      <CircleCenter size="3rem" backgroundColor="white">
        <img
          :src="organizationStore.myOrganizationMediaUrls?.bot_icon_url"
          alt="company-logo"
          @error="organizationStore.useFallbackBotLogoUrl"
        />
      </CircleCenter>
    </div>
    <div class="bot-message-body-container message-body-container">
      <div class="bot-message-title">
        <p>
          <span class="fw-bold">{{ organizationStore.botName }}</span>
        </p>
      </div>
      <p ref="responseEl" v-html="response"></p>
      <div
        v-if="props.messageObj.media_urls.length > 0"
        class="media-urls-container mt-2"
      >
        <div v-for="mediaObj in props.messageObj.media_urls">
          <ImageThumbnail v-if="mediaObj.media_type?.startsWith('image/')" :imageObj="mediaObj" />
          <VideoThumbnail v-else-if="mediaObj.media_type?.startsWith('video/')" :imageObj="mediaObj" />
          <GenericThumbnail v-else :imageObj="mediaObj" />
        </div>
      </div>
      <div v-if="props.isLast" class="rate-response-container mt-2">
        <p>Rate this response</p>
        <button @click="handleRateResponse('good')">
          <Icon icon="thumbs-up" />
        </button>
        <button @click="handleRateResponse('bad')">
          <Icon icon="thumbs-down" />
        </button>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
$--image-thumbnail-width: 200px;

p {
  margin: 0;
}

.bot-message-container {
  display: flex;
  gap: 1em;
  /* border: 1px solid black; */
}

.bot-message-container img {
  height: 2em;
  width: 2em;
}

.bot-message-title {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5em;
  margin-bottom: 0.25em;
}

.media-urls-container {
  max-width: 95%;
  display: grid;
  grid-template-columns: repeat(4, $--image-thumbnail-width);
  gap: 12px;
}

.rate-response-container {
  display: flex;
  align-items: baseline;
  gap: 0.25rem;
  font-style: italic;
  font-size: medium;
}

.rate-response-container button {
  border: none;
  padding: none;
  background: none;
}

.rate-response-container button:hover {
  fill: var(--primary-250);
  stroke: var(--primary-250);
}
</style>
